import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// #36: Ein zentrales Muster, um UI-Zustand (Ansicht + Filter + Auswahl) in der URL-Query zu halten
// — deep-linkbar, reload-fest, teilbar; einmal gelöst statt pro Seite neu erfunden.
//
// M7 (Review-Finding): dieser Kommentar behauptete früher, Freitext-/Suchfelder seien bewusst
// NICHT hierüber persistiert ("jeder Tastenanschlag würde die History zumüllen"). Das ist
// inzwischen überholt — #78/#84 nutzen genau dieses Hook für Freitextsuchen (`q` in
// catalog-page.tsx, `scope_q` in scope-table.tsx) und tun es zu Recht: `{ replace: true }` (siehe
// unten) überschreibt bei jedem Tastenanschlag denselben History-Eintrag statt einen neuen
// anzuhängen — die ursprüngliche Sorge (History-Müll pro Zeichen) trifft dank replace also gar
// nicht zu. Ein Suchbegriff in der URL zu haben ist sogar erwünscht: deep-linkbar wie jeder
// andere Filter, reload-fest, teilbar.
//
// Warum { replace: true }: View-/Filter-/Auswahl-Wechsel sollen die Browser-History nicht
// aufblähen. Der einzige Eintrag, der als History-„push" gehört, ist die Navigation in eine
// Detailseite — und die macht react-router beim Klick auf einen <Link>/navigate() ohnehin selbst.
// Dadurch ergibt sich die Zurück-Navigation aus einer Detailseite von allein: sie landet wieder
// auf der Liste mit exakt dem Filter-/View-Stand, der in der URL stand.
//
// Warum die Setter immer aus den AKTUELLEN searchParams ableiten (functional updater): ein Setzen
// von Schlüssel A darf einen fremden, gleichzeitig in der URL stehenden Schlüssel B nicht löschen.

/**
 * Scalar-String-State in einem Query-Param, reload-fest + teilbar.
 *
 * Fehlt der Param, liefert das Hook `defaultValue`. Wird der Wert auf `defaultValue` gesetzt, wird
 * der Param ENTFERNT (saubere URLs statt `?key=default`). Geschrieben wird mit `{ replace: true }`.
 */
export function useUrlState(key: string, defaultValue: string): [string, (v: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          // Aus den aktuellen Params ableiten, damit andere Query-Keys erhalten bleiben.
          const params = new URLSearchParams(prev);
          if (next === defaultValue) {
            params.delete(key);
          } else {
            params.set(key, next);
          }
          return params;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams],
  );

  return [value, setValue];
}

/**
 * Mehrfachwert-State in EINEM Query-Param (kommagetrennt) — z. B. die Reporting-Plan-Auswahl.
 *
 * Fehlt der Param oder ist er leer, liefert das Hook `[]`. Eine leere Liste ENTFERNT den Param
 * (saubere URLs). Wie `useUrlState` mit `{ replace: true }` und ableitend aus den aktuellen Params.
 */
export function useUrlStateList(key: string): [string[], (v: string[]) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  // Gegen manuell editierte URLs robust: Whitespace je Token entfernen, leere Tokens (`a,,b`) und
  // Duplikate (`a,a`) verwerfen. `togglePlan` erzeugt beides ohnehin nie.
  const value = raw
    ? [
        ...new Set(
          raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ]
    : [];

  const setValue = useCallback(
    (next: string[]) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.length === 0) {
            params.delete(key);
          } else {
            params.set(key, next.join(','));
          }
          return params;
        },
        { replace: true },
      );
    },
    [key, setSearchParams],
  );

  return [value, setValue];
}
